import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal, useProfile, useRemainingTime, useTask, useTaskQue } from '@/hooks';
import { Avatar, Modal } from '@/components';
import { TaskPublicDtoRead } from '@/store/services/MidleNestApi.service';
import { Quests } from '@/types/quests.type';
import SubmitFileView from '@/views/tasks/submitFile.view';
import metamaskSvg from '@/assets/images/metamask.svg';
import { useHandleCompleteTask } from '@/hooks/useHandleCompleteTask.hook';

interface IProps extends TaskPublicDtoRead {
  className?: string;
  customId: string;
  isCompleted?: boolean;
  // titleOnRigh?: boolean;
  showInfo?: boolean;
}

// create a random number

export default function Task({
  className = '',
  customId,
  isCompleted: propsIsCompleted,
  showInfo = true,
  ...props
}: IProps) {
  const { t } = useTranslation();
  const {
    profile: { user },
  } = useProfile();
  const { id: modalId, showModal, hideModal } = useModal(`modal-${props.id}`);

  const {
    id: submitModalId,
    showModal: submitShowModal,
    hideModal: submitHideModal,
  } = useModal(`submitModal-${props.id}-${customId}`);

  const { isCustomerWaiting } = useTaskQue(props.id);

  const { check, isWaiting, isCompleted: _isCompleted } = useTask(String(props.id));
  const isCompleted = propsIsCompleted || _isCompleted;

  const [isActive, setIsActive] = useState<boolean>(false);
  const [counter, setCounter] = useState<number>(0);
  const [disabled, setDisabled] = useState<boolean>(false);

  // const now = new Date();
  // const oneYearLater = now.setFullYear(now.getFullYear() + 1);

  const remainingTime = useRemainingTime(props.endDate);

  const platform = props.platform;

  const handleCompleteTask = useHandleCompleteTask({
    task: props,
    setIsActive,
    showTaskModal: showModal,
    showSubmitModal: submitShowModal,
  });

  const handleCheck = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    setCounter(counter + 1);
    if (counter > 5) {
      setDisabled(true);
      return;
    }
    const actionType = props.action as Quests;
    if (user.id && !disabled) {
      check(actionType)(props.id as string, () => {
        // TODO: TASK COMPLETE
        gtag('event', 'task-complete', {
          task: props.id,
          user: user?.id,
          project: props.customer.id,
          platform: props.platform,
          action: props.action,
          xp: String(props.xp),
        });
      });
    }
  };

  useEffect(() => {
    // TODO: TASK VIEW/IMPRESSION
    // if (user?.id)
    gtag('event', 'task-view', {
      task: props.id,
      user: user?.id,
      project: props.customer.id,
      platform: props.platform,
      action: props.action,
    });
    if (isWaiting) setTimeout(() => setIsActive(true), 5000);
  }, []);

  const logo = props.avatar ? props.avatar.src : props.customer?.profile?.avatar?.src;

  return (
    <>
      {/* {!titleOnRight && ( */}
      {/*   <p className='dark:text-white font-medium text-sm pb-1 min-w-[250px]'>{title?.en}</p> */}
      {/* )} */}
      {/*<p className='font-medium text-sm pt-4 pb-2 xl:hidden lg:block sm:hidden'>{title}</p>*/}
      {/*<div className='flex items-center justify-items-stretch w-full pb-4 xl:pt-4 lg:pt-0 sm:pt-4 relative'>*/}
      <div
        data-platform={props.platform}
        data-action={props.action}
        data-action-type={props.action}
        className={`w-full flex space-x-2 items-center justify-between cursor-pointer ${className}`}
        onClick={() => showModal()}
      >
        <div className='flex-1 truncate flex items-center'>
          <div>
            <Avatar name={String(props.title?.en)} src={logo} className='w-12 h-12 mr-2' />
          </div>

          <div className='flex-1 w-full truncate'>
            <div className='dark:text-white max-w-[90%] font-medium text-sm pb-1 truncate'>
              {props.title?.en}
            </div>
            {showInfo && (
              <div>
                {props.xp != 0 && (
                  <span className='block font-medium text-sm text-green-500'>
                    {props.xp} {t('points')}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* <div className='flex-1 flex flex-col bg-blue-500'> */}
          {/* </div> */}
        </div>

        <div>
          {isCompleted ? (
            <button type='button' className='btn task task-completed'>
              {t('Completed')}
              <i className='icon-check ml-1' />
            </button>
          ) : isWaiting ? (
            <button
              type='button'
              onClick={handleCheck}
              className={`btn task task-verify ${isActive ? 'active' : ''}`}
              disabled={!isActive || disabled}
            >
              {t('Verify')}
              <i className='icon-clock ml-1' />
              <span className='progress'>
                <span className='bar' />
              </span>
            </button>
          ) : isCustomerWaiting ? (
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();

                if (props.platform === 'screenshot') {
                  handleCompleteTask();
                }
              }}
              className='btn task task-pending'
            >
              {t('Pending')}
              {/*<i className='icon-metamask ml-1' />*/}
            </button>
          ) : (
            <button
              type='button'
              onClick={handleCompleteTask}
              className='btn task task-complete dark:text-[#FFDA68]'
            >
              {t('Complete')}
              <img
                className='max-w-[16px] max-h-4 ml-2'
                src={
                  platform === 'onchain'
                    ? metamaskSvg
                    : `/platforms/${platform === 'screenshot' ? 'proofofwork' : platform}.svg`
                }
                alt='platform'
              />
            </button>
          )}
        </div>
      </div>

      {/*  TASK MODAL --- */}
      <Modal id={modalId}>
        <i
          className='absolute right-4  top-4 icon-close text-2xl text-gray-400 cursor-pointer'
          onClick={hideModal}
        />
        <div className='w-full max-w-full sm:w-80 flex flex-col items-center  '>
          <div className='mb-4'>
            <Avatar name={String(props.title)} src={String(logo)} className='w-16 h-16' />
          </div>
          <h2 className='font-semibold mb-2'>{props.title?.en}</h2>
          <h2 className='font-semibold mb-2'>{props?.description?.en || '-'}</h2>
          {!!props.xp && (
            <div className='w-full my-4 grid grid-cols-2 gap-4 '>
              <div className='border p-4 flex flex-col items-center rounded-xl'>
                <label>{t('Points')}</label>
                <span className='text-3xl font-bold'>{props.xp}</span>
              </div>
              <div className='border p-4 flex flex-col items-center rounded-xl'>
                <label>{t('User Limit')}</label>
                <span className='text-3xl font-bold'>
                  {props.participantsLimit
                    ? `${props.participantsCount}/${props.participantsLimit}`
                    : '∞'}
                </span>
              </div>
            </div>
          )}
          {/*<strong>{t('Task Condition')}</strong>*/}
          {/*<div className='w-full flex items-center justify-start'>*/}
          {/*  <i className={`icon-${props.platform || 'link'} text-3xl `} />*/}
          {/*  <QuestName platform={String(props.platform)} action={String(props.action)} />*/}
          {/*</div>*/}
          {/*<strong>{t('Task Description')}</strong>*/}
          {props.description && (
            <p className='mb-4 w-full p-4 text-sm bg-gray-50 dark:bg-transparent border rounded-xl font-normal'>
              {props.description?.en}
            </p>
          )}

          {!!remainingTime.time && (
            <div className='w-full p-4 mb-4 border rounded-xl text-center font-semibold'>
              {t('Remaining Time')}
              <div className='w-full p-4 flex items-center justify-center space-x-3 xl:space-x-2'>
                <div className='flex flex-col items-center justify-center'>
                  <span className='text-2xl font-bold lg:text-3xl'>{remainingTime?.days}</span>
                  <label className='text-xs  font-normal lg:text-base'>{t('Days')}</label>
                </div>
                <span className='font-bold text-xl lg:text-2xl'>:</span>
                <div className='flex flex-col items-center justify-center'>
                  <span className='text-2xl font-bold lg:text-3xl'>{remainingTime?.hours}</span>
                  <label className='text-xs  font-normal lg:text-base'>{t('Hours')}</label>
                </div>
                <span className='font-bold text-xl lg:text-2xl'>:</span>
                <div className='flex flex-col items-center justify-center'>
                  <span className='text-2xl font-bold lg:text-3xl'>{remainingTime?.minutes}</span>
                  <label className='text-xs  font-normal lg:text-base '>{t('Minutes')}</label>
                </div>
              </div>
            </div>
          )}

          {isCompleted ? (
            <button
              type='button'
              className='w-full flex items-center justify-center px-3 py-2 text-sm text-white bg-blue-500 dark:bg-[#FFDA68] dark:outline-none dark:text-[#151515] outline-none border dark:border-none border-blue-500 rounded-lg'
            >
              {t('Completed')}
              <i className='icon-check ml-1' />
            </button>
          ) : isWaiting ? (
            <button
              type='button'
              onClick={handleCheck}
              className={`btn task task-verify ${isActive ? 'active' : ''} !w-full`}
              disabled={!isActive || disabled}
            >
              {t('Verify')}
              <i className='icon-clock ml-1' />
              <span className='progress'>
                <span className='bar' />
              </span>
              <i className='icon-check ml-1' />
            </button>
          ) : (
            <button
              type='button'
              onClick={handleCompleteTask}
              className='w-full  flex items-center justify-center px-3 py-2 text-sm text-blue-500 border border-blue-500 dark:bg-[#FFDA68] dark:border-none dark:text-[#151515] rounded-lg'
            >
              {t('Complete')}
              {/*<i className='icon-metamask ml-1' />*/}
            </button>
          )}
        </div>
      </Modal>
      <Modal
        id={submitModalId}
        onClose={() => {
          const _t = localStorage.getItem('task-customer-waiting')?.split(',')?.includes(props.id);
          if (_t) location.reload();
          submitHideModal();
        }}
      >
        <i
          className='absolute right-4  top-4 icon-close text-2xl text-gray-400 cursor-pointer'
          onClick={() => {
            const _t = localStorage
              .getItem('task-customer-waiting')
              ?.split(',')
              ?.includes(props.id);
            if (_t) location.reload();
            submitHideModal();
          }}
        />

        <SubmitFileView
          taskId={props.id}
          visitUrl={props.visitUrl || ''}
          title={props.title.en}
          description={props.description?.tr}
          avatar={logo}
          endDate={props.endDate}
        />
      </Modal>
    </>
  );
}
